// weather/card.jsx — the weather summary on the home screen.
//
// It answers "what is the rest of today like", not "what is it now":
// anyone at the panel can see outside, and the outside temperature
// already sits in the house header beside the inside one. So it leads
// with BOM's own summary and the rain, then the next few hours. From
// 5 pm it describes tonight and tomorrow (model.js `summarise`).
//
// Rain has no colour (Ben, 2026-10-22: try it without blue first). The
// chance is text whose weight and tone rise with it.
//
// The whole card opens the Weather page.

import * as React from 'react';
import { A_FONT, fs } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { Degrees } from '../degrees.jsx';
import { WeatherIcon } from './icons.jsx';
import { rainLine, fmtHour, asHeading } from './model.js';

export function chanceStyle(T, chance) {
  if (chance == null || chance < 20) return { color: 'transparent' };
  if (chance >= 50) return { color: T.ink, fontWeight: 600 };
  return { color: T.inkSoft, fontWeight: 500 };
}

export function WarningLine({ T, warnings, style }) {
  if (!warnings?.length) return null;
  const w = warnings[0];
  const more = warnings.length - 1;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      fontFamily: A_FONT.sans, fontSize: fs(T, 14), fontWeight: 600, color: T.attention,
      ...style,
    }}>
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: T.attention, flexShrink: 0 }}/>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {w.title}{more > 0 ? ` and ${more} more` : ''}
      </span>
    </div>
  );
}

export function WeatherCard({ T, weather, onOpen, compact }) {
  const { summary, hours, warnings, ready } = weather;
  const shown = hours.slice(0, compact ? 5 : 7);

  const shell = {
    display: 'flex', flexDirection: 'column', gap: 14, width: '100%',
    padding: compact ? '16px 18px' : '18px 22px', height: '100%', boxSizing: 'border-box',
    background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
    border: 'none', textAlign: 'left', color: T.ink, fontFamily: A_FONT.sans,
  };

  if (!ready || !summary) {
    return (
      <div style={{ ...shell, justifyContent: 'center', minHeight: 120 }}>
        <div style={{ fontSize: fs(T, 16), fontWeight: 500 }}>Weather unavailable</div>
        <div style={{ fontSize: fs(T, 13), color: T.inkSoft }}>
          The Bureau of Meteorology forecast isn't reaching Home Assistant.
        </div>
      </div>
    );
  }

  const rain = rainLine(summary.rainChance, summary.rainMin, summary.rainMax);

  return (
    <button onClick={onOpen} aria-label="Open the weather forecast" style={{ ...shell, cursor: 'pointer' }}>
      <WarningLine T={T} warnings={warnings}/>
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 16 : 24, width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 0', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: T.ink }}><WeatherIcon name={summary.icon} size={34} bg={T.card}/></span>
            <div style={{ minWidth: 0 }}>
              {summary.when !== 'Today' && (
                <div style={{ fontSize: fs(T, 13), color: T.inkSoft, fontWeight: 500 }}>{summary.when}</div>
              )}
              <div style={{ fontSize: fs(T, 18), fontWeight: 600, lineHeight: 1.2 }}>{asHeading(summary.short)}</div>
            </div>
          </div>
          {rain && <div style={{ fontSize: fs(T, 14), color: T.ink }}>{rain}</div>}
          <div style={{ display: 'flex', gap: 16, alignItems: 'baseline', fontSize: fs(T, 14), color: T.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
            {summary.pair.filter((p) => p.value != null).map((p) => (
              <span key={p.label}>{p.label} <span style={{ color: T.ink, fontWeight: 500 }}>{Math.round(p.value)}°</span></span>
            ))}
          </div>
        </div>

        {shown.length > 0 && (
          <>
            <div style={{ width: 1, alignSelf: 'stretch', background: T.hair, flexShrink: 0 }}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flex: compact ? '1.3 1 0' : '1.6 1 0', minWidth: 0 }}>
              {shown.map((h, i) => (
                <div key={h.t.toISOString()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 34 }}>
                  <div style={{ fontSize: fs(T, 13), color: T.inkSoft, fontVariantNumeric: 'tabular-nums' }}>{i === 0 ? 'Now' : fmtHour(h.t)}</div>
                  <span style={{ color: T.ink }}><WeatherIcon name={h.icon} size={22} bg={T.card}/></span>
                  <div style={{ fontSize: fs(T, 15), fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {h.temp != null ? `${Math.round(h.temp)}°` : '–'}
                  </div>
                  <div style={{ fontSize: fs(T, 12), fontVariantNumeric: 'tabular-nums', ...chanceStyle(T, h.chance) }}>
                    {h.chance != null ? `${h.chance}%` : '0%'}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </button>
  );
}

// The outside reading for the house header: "18° outside". Null when the
// station reading is missing or stale, and the header then leaves it out.
export function OutsideReading({ T, weather, num, word }) {
  const t = weather?.current?.temp;
  if (t == null) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <Degrees T={T} value={t} size={num}/>
      <span style={word}>outside</span>
    </span>
  );
}
